/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import ActionSubjectsService from 'cio/services/action-subjects';
import CampaignModel from 'cio/models/campaign';
import { computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import { GROUP_COLORS } from 'cio/utils/action-info';

interface VisualWorkflowBuilderV2BulkDeleteWarningModalArgs {
  campaign: CampaignModel;
  highlightedNodeIds: string[];
  isDisabled: boolean;
  onConfirm: () => unknown;
  onClose: () => unknown;
  hasReadOnlyPermission: boolean;
}

const GROUP_META: Record<
  string,
  { color: string; label: string; group: string; description: string; isDelay?: boolean; subjectsDescription?: string }
> = {
  branches: {
    label: 'branch',
    color: GROUP_COLORS.flow_control,
    group: 'branches',
    description: 'deletes all associated branch content and settings',
  },
  messages: {
    label: 'message',
    color: GROUP_COLORS.messages,
    group: 'messages',
    description: 'deletes all message content, settings and report data',
  },
  exits: {
    label: 'exit',
    color: GROUP_COLORS.flow_control,
    group: 'exits',
    description: 'deletes exits',
  },
  data: {
    label: 'data action',
    color: GROUP_COLORS.data,
    group: 'data',
    description: 'deletes all data content, settings and report data',
  },
  timeDelaysAndWindows: {
    label: 'delay and window',
    color: GROUP_COLORS.delays,
    group: 'timeDelaysAndWindows',
    isDelay: true,
    description: 'deletes delays',
    subjectsDescription: 'people waiting continue to the next action',
  },
  waitUntils: {
    label: 'wait until',
    color: GROUP_COLORS.delays,
    group: 'waitUntils', // eslint-disable-line spellcheck/spell-checker
    isDelay: true,
    description: 'deletes wait untils', // eslint-disable-line spellcheck/spell-checker
  },
};

// eslint-disable-next-line spellcheck/spell-checker
const GROUP_ORDER: string[] = ['branches', 'messages', 'exits', 'data', 'timeDelaysAndWindows', 'waitUntils'];

export default class VisualWorkflowBuilderV2BulkDeleteWarningModal extends Component<VisualWorkflowBuilderV2BulkDeleteWarningModalArgs> {
  @service actionSubjects!: ActionSubjectsService;
  @service store!: DS.Store;

  @tracked waitUntilDeleteOption: 'continue' | 'exitEarly' | '' = '';

  get groupMeta() {
    if (this.args.campaign.isPeopleCampaign) {
      return GROUP_META;
    }

    return Object.keys(GROUP_META).reduce<Record<string, (typeof GROUP_META)[string]>>((acc, key) => {
      let meta = { ...GROUP_META[key] };
      if (meta.subjectsDescription != null) {
        meta.subjectsDescription = meta.subjectsDescription.replace('people', 'objects');
      }
      acc[key] = meta;
      return acc;
    }, {});
  }

  get delayActionsSubjectCounts() {
    return this.args.highlightedNodeIds.reduce(
      (acc, id) => {
        let actionModel = this.store.peekRecord('action', id);
        if (!actionModel?.isDelay) {
          return acc;
        }

        let subjectCount = this.actionSubjects.find(id)?.count;

        if (!subjectCount) {
          return acc;
        }

        if (actionModel.isTimeDelay) {
          acc.timeDelaysAndWindows += subjectCount;
          acc.timeDelays += subjectCount;
        }

        if (actionModel.isTimeWindow) {
          acc.timeDelaysAndWindows += subjectCount;
          acc.timeWindows += subjectCount;
        }

        if (actionModel.isWaitUntil) {
          acc.waitUntils += subjectCount;
        }

        return acc;
      },
      {
        timeDelays: 0,
        timeWindows: 0,
        timeDelaysAndWindows: 0,
        waitUntils: 0,
      } as Record<string, number>,
    );
  }

  // eslint-disable-next-line spellcheck/spell-checker
  @computed('args.campaign.isRunning', 'delayActionsSubjectCounts.waitUntils', 'waitUntilDeleteOption')
  get isSubmitDisabled() {
    return (
      this.args.campaign.isRunning &&
      this.delayActionsSubjectCounts.waitUntils > 0 &&
      isEmpty(this.waitUntilDeleteOption)
    );
  }

  get actionGroupCounts() {
    return this.args.highlightedNodeIds.reduce(
      (counts, id) => {
        let actionModel = this.store.peekRecord('action', id);
        if (!actionModel) {
          return counts;
        }

        if (actionModel.isExit) {
          counts.exits += 1;
        } else if (actionModel.info.group === 'data') {
          counts.data += 1;
        } else if (actionModel.isMessagingType || actionModel.isSplitTest) {
          counts.messages += 1;
        } else if (actionModel.isTimeDelay) {
          counts.timeDelaysAndWindows += 1;
          counts.timeDelays += 1;
        } else if (actionModel.isTimeWindow) {
          counts.timeDelaysAndWindows += 1;
          counts.timeWindows += 1;
        } else if (actionModel.isWaitUntil) {
          counts.waitUntils += 1;
        } else if (actionModel.isBranch) {
          counts.branches += 1;
        }

        return counts;
      },
      {
        branches: 0,
        messages: 0,
        exits: 0,
        data: 0,
        timeDelays: 0,
        timeWindows: 0,
        timeDelaysAndWindows: 0,
        waitUntils: 0,
      } as Record<string, number>,
    );
  }

  get actionGroupCountItems() {
    return GROUP_ORDER.filter((c) => this.actionGroupCounts[c] > 0).map((c) => ({
      ...this.groupMeta[c],
      count: this.actionGroupCounts[c],
    }));
  }
}
